import { LatLon, distanceTo, getBoundingBox } from "geolocation-utils";

import { Utils } from "utils";
import { IFirestoreRouteDay, IRouteDay, IRouteIFrameRequest } from "types";

// Implementation of circle packing throuth a route
export const packCircles = ({
  gap = 0,
  coordinates,
  circleRadius = 50,
}: {
  gap?: number; // (km)
  circleRadius?: number; // (km)
  coordinates: [number, number][]; // [lat, lng]
}) => {
  const startFinishGap = gap + circleRadius; // (km);

  // Calculate Route distance
  const distances: [number, number, number][] = [];
  coordinates.forEach((point, index) => {
    if (index !== coordinates.length - 1) {
      const [startLat, startLng] = point;
      const [nextLat, nextLng] = coordinates[index + 1];

      const legDistance = distanceTo(
        { lat: startLat, lon: startLng },
        { lat: nextLat, lon: nextLng }
      );
      distances.push([index, index + 1, legDistance]);
    }
  });
  const pathDistance = distances.reduce((sum, element) => sum + element[2], 0);
  const pathDistanceInKm = Number((pathDistance / 1000).toFixed(2));

  const isRadiusBiggerThenRoute = startFinishGap > pathDistanceInKm / 2;

  if (isRadiusBiggerThenRoute) {
    const start = coordinates[0];
    const finish = coordinates[coordinates.length - 1];

    const boundinBox = getBoundingBox(
      [
        { lat: start[0], lon: start[1] },
        { lat: finish[0], lon: finish[1] },
      ],
      0
    );

    const topLeft = boundinBox.topLeft as unknown as LatLon;
    const bottomRight = boundinBox.bottomRight as unknown as LatLon;

    const lat1 = topLeft.lat;
    const lng1 = topLeft.lon;
    const lat2 = bottomRight.lat;
    const lng2 = bottomRight.lon;

    const centerLat = (lat1 + lat2) / 2;
    const centerLng = (lng1 + lng2) / 2;

    const middlePoint = { lat: centerLat, lng: centerLng };

    return {
      circles: [
        {
          ...middlePoint,
          radius:
            distanceTo(
              { lat: start[0], lng: start[1] },
              { lat: finish[0], lng: finish[1] }
            ) / 2,
        },
      ],
      totalDistance: pathDistance,
      distanceWithGaps: 0,
    };
  }

  // Make a gap with 'startFinishGap' value from start/finish points;
  const gapDistances: [number, number, number][] = [];

  distances.forEach((item, index) => {
    const gap = startFinishGap * 1000;

    const passedDistance = distances
      .slice(0, index)
      .reduce((s, el) => s + el[2], 0);
    const remainingDistance = distances
      .slice(index, -1)
      .reduce((s, el) => s + el[2], 0);

    if (passedDistance > gap && remainingDistance > gap) {
      gapDistances.push(item);
    }
  });

  const gapDistance = gapDistances.reduce((sum, el) => sum + el[2], 0);
  //   const gapDistanceInKm = (gapDistance / 1000).toFixed(2);

  // Generate array of circles center;
  const circlesCenters: { lat: number; lng: number; radius: number }[] = [];
  gapDistances.forEach((item, index) => {
    if (index === 0) {
      const [lat, lng] = [coordinates[item[1]][1], coordinates[item[1]][0]];
      circlesCenters.push({ lat, lng, radius: circleRadius * 1000 });
    } else {
      const [lat, lng] = coordinates[item[1]];
      const lastCircle = circlesCenters.at(-1)!;
      const distance = distanceTo(
        { lat, lon: lng },
        { lat: lastCircle.lat, lon: lastCircle.lng }
      );

      if (distance >= circleRadius * 1000) {
        circlesCenters.push({ lat, lng, radius: circleRadius * 1000 });
      }

      if (index === gapDistances.length - 1) {
        const reversed = distances.reverse();
        let lastItemIndex = 0;

        reversed.forEach((item, index) => {
          const gap = startFinishGap * 1000;

          const passedDistance = reversed
            .slice(0, index)
            .reduce((s, el) => s + el[2], 0);
          // const remainingDistance = distances
          //   .slice(index, -1)
          //   .reduce((s, el) => s + el[2], 0);

          if (passedDistance > gap && lastItemIndex === 0) {
            lastItemIndex = item[1];
            // gapDistances.push(item);
          }
        });

        // console.log({ lastItemIndex });

        const lastCoords = coordinates[lastItemIndex];
        circlesCenters.push({
          lat: lastCoords[0],
          lng: lastCoords[1],
          radius: circleRadius * 1000,
        });
      }
    }
  });

  return {
    circles: circlesCenters,
    totalDistance: pathDistance,
    distanceWithGaps: gapDistance,
  };
};

export const isRequestFullfilled = (request?: IRouteIFrameRequest) => {
  let isPointsFilled = false;
  let isDurationAndAvoidanceFilled = false;
  let isTagsFilled = false;

  if (request) {
    isPointsFilled =
      Boolean(request.start && request.finish) ||
      Boolean(request.start && request.radius && request.radius > 0);

    isDurationAndAvoidanceFilled =
      Boolean(request.roadType) && Boolean(request.duration);

    isTagsFilled =
      request.roadType === "twisty"
        ? true
        : Object.keys(request.tags)
            .map((k) => request.tags[k as keyof typeof request.tags])
            .flat(1).length > 0;
  }

  return {
    tags: isTagsFilled,
    points: isPointsFilled,
    duration: isDurationAndAvoidanceFilled,
  };
};

export const convertToRoute = (data: IFirestoreRouteDay) => {
  const converted: IRouteDay = {
    hotels: data.hotels || [],
    campings: [],
    allPlaces: [],
    suggestedPlaces: [],
    totalTime: data.totalTime,
    totalDistance: data.totalDistance,
    sameRoads: Object.values(data.sameRoads),
    selectedWaypoints: data.selectedWaypoints,
    bestBikingRoads: Object.values(data.bestBikingRoads),
    points: data.coordinates.map(Utils.Coordinates.convert.latLng.toPoint),
    selectedPlaces: data.selectedPlaces.map((item) =>
      item === null ? undefined : item
    ),
  };

  return converted;
};
